import React, { useState, useEffect } from 'react';
import { api, API_BASE, logHttpError } from '../services/api';
import { useNavigate } from 'react-router-dom';
import StickerEditorModal from '../components/stickers/StickerEditorModal';

interface StickerPack {
  id: number;
  name: string;
  description: string | null;
  creator_id: number;
  creator_username: string;
  is_public: boolean;
  created_at: string;
  stickers_count: number;
}

interface Sticker {
  id: number;
  pack_id: number;
  image_url: string;
  created_at: string;
}

const StickerPacksPage: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('sharkly_user_id');

  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [myPacks, setMyPacks] = useState<StickerPack[]>([]);
  const [selectedPack, setSelectedPack] = useState<StickerPack | null>(null);
  const [stickers, setStickers] = useState<Sticker[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newPackName, setNewPackName] = useState('');
  const [newPackDescription, setNewPackDescription] = useState('');
  const [editingPack, setEditingPack] = useState<StickerPack | null>(null);
  const [editingSticker, setEditingSticker] = useState<Sticker | null>(null);
  const [editedPackName, setEditedPackName] = useState('');
  const [uploadingSticker, setUploadingSticker] = useState(false);

  useEffect(() => {
    if (!userId) {
      navigate('/login');
      return;
    }
    void refreshPacks();
  }, [userId]);

  const fetchPacks = async (): Promise<boolean> => {
    try {
      const res = await api.get('/stickers/packs');
      setPacks(res.data);
      return true;
    } catch (err) {
      logHttpError("Ошибка загрузки стикерпаков", err);
      setLoadError("Не удалось загрузить публичные стикерпаки.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPacks = async (): Promise<boolean> => {
    try {
      const res = await api.get('/stickers/my-packs');
      setMyPacks(res.data);
      return true;
    } catch (err) {
      logHttpError("Ошибка загрузки моих стикерпаков", err);
      setLoadError("Не удалось загрузить ваши стикерпаки.");
      return false;
    }
  };

  const fetchPackStickers = async (packId: number): Promise<boolean> => {
    try {
      const res = await api.get(`/stickers/packs/${packId}/stickers`);
      setStickers(res.data);
      return true;
    } catch (err) {
      logHttpError("Ошибка загрузки стикеров", err);
      setLoadError("Не удалось загрузить стикеры выбранного пака.");
      return false;
    }
  };

  const refreshPacks = async (): Promise<boolean> => {
    setLoadError(null);
    const [publicPacksLoaded, ownPacksLoaded] = await Promise.all([
      fetchPacks(),
      fetchMyPacks(),
    ]);
    return publicPacksLoaded && ownPacksLoaded;
  };

  const notifyIncompleteRefresh = () => {
    alert("Изменение сохранено, но список не удалось обновить. Попробуйте обновить страницу.");
  };

  const retryLoad = async () => {
    await refreshPacks();
    if (selectedPack) await fetchPackStickers(selectedPack.id);
  };

  const isOwnPack = (pack: StickerPack) => myPacks.some((ownPack) => ownPack.id === pack.id);

  const handleCreatePack = async () => {
    if (!newPackName.trim()) return;

    try {
      await api.post('/stickers/packs', {
        name: newPackName,
        description: newPackDescription
      });

      setNewPackName('');
      setNewPackDescription('');
      setShowCreateModal(false);
      if (!await refreshPacks()) notifyIncompleteRefresh();
    } catch (err) {
      logHttpError("Ошибка создания пака", err);
      alert("Не удалось создать стикерпак");
    }
  };

  const handleUploadSticker = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedPack) return;

    try {
      setUploadingSticker(true);
      const formData = new FormData();
      formData.append('file', file);

      await api.post(`/stickers/packs/${selectedPack.id}/stickers`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      const [stickersLoaded, packsLoaded] = await Promise.all([
        fetchPackStickers(selectedPack.id),
        refreshPacks(),
      ]);

      // Очищаем input и закрываем модалку после успешной загрузки
      (e.target as HTMLInputElement).value = '';
      setShowUploadModal(false);
      if (stickersLoaded && packsLoaded) {
        alert("Стикер успешно добавлен!");
      } else {
        notifyIncompleteRefresh();
      }
    } catch (err) {
      logHttpError("Ошибка загрузки стикера", err);
      alert("Не удалось загрузить стикер");
    } finally {
      setUploadingSticker(false);
    }
  };

  const handleAddPack = async (packId: number) => {
    try {
      await api.post(`/stickers/packs/${packId}/add`);
      if (await refreshPacks()) {
        alert("Стикерпак добавлен в вашу коллекцию!");
      } else {
        notifyIncompleteRefresh();
      }
    } catch (err) {
      logHttpError("Ошибка добавления пака", err);
      alert("Этот стикерпак уже добавлен или произошла ошибка");
    }
  };

  const handleRenamePack = async () => {
    if (!editingPack || !editedPackName.trim()) return;

    try {
      await api.put(`/stickers/pack/${editingPack.id}`, {
        name: editedPackName.trim(),
      });

      setSelectedPack((pack) => (
        pack?.id === editingPack.id
          ? { ...pack, name: editedPackName.trim() }
          : pack
      ));
      setEditingPack(null);
      setEditedPackName('');
      if (!await refreshPacks()) notifyIncompleteRefresh();
    } catch (err) {
      logHttpError("Ошибка переименования пака", err);
      alert("Не удалось переименовать стикерпак");
    }
  };

  const handleDeletePack = async (pack: StickerPack) => {
    if (!window.confirm(`Удалить стикерпак «${pack.name}» и все его стикеры?`)) {
      return;
    }

    try {
      await api.delete(`/stickers/pack/${pack.id}`);

      if (selectedPack?.id === pack.id) {
        setSelectedPack(null);
        setStickers([]);
      }
      if (!await refreshPacks()) notifyIncompleteRefresh();
    } catch (err) {
      logHttpError("Ошибка удаления пака", err);
      alert("Не удалось удалить стикерпак");
    }
  };

  const handleDeleteSticker = async (sticker: Sticker) => {
    if (!selectedPack || !window.confirm("Удалить этот стикер?")) return;

    try {
      await api.delete(`/stickers/sticker/${sticker.id}`);
      const [stickersLoaded, packsLoaded] = await Promise.all([
        fetchPackStickers(selectedPack.id),
        refreshPacks(),
      ]);
      if (!stickersLoaded || !packsLoaded) notifyIncompleteRefresh();
    } catch (err) {
      logHttpError("Ошибка удаления стикера", err);
      alert("Не удалось удалить стикер");
    }
  };

  const handleStickerSaved = () => {
    if (!selectedPack) return;
    void (async () => {
      const [stickersLoaded, packsLoaded] = await Promise.all([
        fetchPackStickers(selectedPack.id),
        refreshPacks(),
      ]);
      if (!stickersLoaded || !packsLoaded) notifyIncompleteRefresh();
    })();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0f1115] text-white">
        <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f1115] text-white pb-20">
      <main className="max-w-6xl mx-auto p-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-4xl font-black">🎨 Стикерпаки</h1>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-bold transition shadow-[0_0_15px_rgba(37,99,235,0.4)]"
          >
            + Создать пак
          </button>
        </div>

        {loadError && (
          <div role="alert" className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-red-800 bg-red-950/50 px-4 py-3 text-red-200">
            <span>{loadError}</span>
            <button
              onClick={() => void retryLoad()}
              className="shrink-0 rounded-lg bg-red-800 px-3 py-2 text-sm font-bold hover:bg-red-700"
            >
              Повторить
            </button>
          </div>
        )}

        {/* Мои стикерпаки */}
        {myPacks.length > 0 && (
          <div className="mb-10">
            <h2 className="text-2xl font-bold mb-4">Мои стикерпаки</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {myPacks.map(pack => (
                <div
                  key={pack.id}
                  onClick={() => {
                    setSelectedPack(pack);
                    fetchPackStickers(pack.id);
                  }}
                  className="bg-[#1a1d24] p-6 rounded-2xl border border-gray-800 hover:border-blue-500 cursor-pointer transition group"
                >
                  <h3 className="text-xl font-bold mb-2 group-hover:text-blue-400">{pack.name}</h3>
                  <p className="text-sm text-gray-400 mb-4">{pack.description || 'Нет описания'}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{pack.stickers_count} стикеров</span>
                    {isOwnPack(pack) && (
                      <div className="flex gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingPack(pack);
                            setEditedPackName(pack.name);
                          }}
                          className="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm font-bold transition"
                        >
                          Переименовать
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleDeletePack(pack);
                          }}
                          className="px-3 py-2 bg-red-700 hover:bg-red-600 rounded-lg text-sm font-bold transition"
                        >
                          Удалить
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedPack(pack);
                            setShowUploadModal(true);
                          }}
                          className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold transition"
                        >
                          + Стикер
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Все публичные стикерпаки */}
        <div>
          <h2 className="text-2xl font-bold mb-4">Все стикерпаки</h2>
          {packs.length === 0 ? (
            <div className="text-center py-20 bg-[#1a1d24] rounded-2xl border border-gray-800 border-dashed">
              <p className="text-4xl mb-3">🎨</p>
              <p className="text-gray-400">Пока нет стикерпаков. Создай первый!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packs.map(pack => (
                <div
                  key={pack.id}
                  onClick={() => {
                    setSelectedPack(pack);
                    fetchPackStickers(pack.id);
                  }}
                  className="bg-[#1a1d24] p-6 rounded-2xl border border-gray-800 hover:border-blue-500 cursor-pointer transition group"
                >
                  <h3 className="text-xl font-bold mb-2 group-hover:text-blue-400">{pack.name}</h3>
                  <p className="text-sm text-gray-400 mb-2">{pack.description || 'Нет описания'}</p>
                  <p className="text-xs text-gray-500 mb-4">Автор: {pack.creator_username}</p>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-gray-500">{pack.stickers_count} стикеров</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAddPack(pack.id);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-bold transition"
                    >
                      Добавить
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Модальное окно просмотра пака */}
        {selectedPack && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d24] rounded-3xl border border-gray-800 max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col">
              <div className="p-6 border-b border-gray-800 flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold">{selectedPack.name}</h2>
                  <p className="text-sm text-gray-400">{selectedPack.description}</p>
                </div>
                <div className="flex items-center gap-3">
                  {isOwnPack(selectedPack) && (
                    <button
                      onClick={() => setShowUploadModal(true)}
                      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-lg text-sm font-bold transition"
                    >
                      + Добавить стикер
                    </button>
                  )}
                  <button
                    onClick={() => setSelectedPack(null)}
                    className="text-gray-400 hover:text-white text-2xl"
                  >
                    ✕
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-6">
                {stickers.length === 0 ? (
                  <p className="text-center text-gray-500 py-10">В этом паке пока нет стикеров</p>
                ) : (
                  <div className="grid grid-cols-4 md:grid-cols-6 gap-4">
                    {stickers.map(sticker => (
                      <div key={sticker.id} className="relative">
                        <img
                          src={`${API_BASE}${sticker.image_url}`}
                          alt="Sticker"
                          className="w-full h-auto rounded-xl border border-gray-700 hover:border-blue-500 transition cursor-pointer bg-[#0f1115]"
                        />
                        {isOwnPack(selectedPack) && (
                          <>
                            <button
                              onClick={() => setEditingSticker(sticker)}
                              className="absolute top-1 left-1 rounded-full bg-blue-700/90 px-2 py-1 text-xs font-bold hover:bg-blue-600"
                              aria-label="Редактировать стикер"
                              title="Редактировать"
                            >
                              ✎
                            </button>
                            <button
                              onClick={() => void handleDeleteSticker(sticker)}
                              className="absolute top-1 right-1 w-7 h-7 bg-red-700/90 hover:bg-red-600 rounded-full text-sm font-bold"
                              aria-label="Удалить стикер"
                              title="Удалить стикер"
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно создания пака */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d24] rounded-3xl border border-gray-800 p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Создать стикерпак</h2>
              <input
                type="text"
                value={newPackName}
                onChange={(e) => setNewPackName(e.target.value)}
                placeholder="Название пака"
                className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white mb-3 focus:border-blue-500 outline-none"
              />
              <textarea
                value={newPackDescription}
                onChange={(e) => setNewPackDescription(e.target.value)}
                placeholder="Описание (необязательно)"
                className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white mb-4 focus:border-blue-500 outline-none resize-none h-24"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition"
                >
                  Отмена
                </button>
                <button
                  onClick={handleCreatePack}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 rounded-xl font-bold transition"
                >
                  Создать
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно переименования пака */}
        {editingPack && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d24] rounded-3xl border border-gray-800 p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Переименовать стикерпак</h2>
              <input
                type="text"
                value={editedPackName}
                onChange={(e) => setEditedPackName(e.target.value)}
                placeholder="Название пака"
                className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white mb-4 focus:border-blue-500 outline-none"
                autoFocus
              />
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setEditingPack(null);
                    setEditedPackName('');
                  }}
                  className="flex-1 px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition"
                >
                  Отмена
                </button>
                <button
                  onClick={() => void handleRenamePack()}
                  disabled={!editedPackName.trim()}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 rounded-xl font-bold transition"
                >
                  Сохранить
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Модальное окно загрузки стикера */}
        {showUploadModal && selectedPack && (
          <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-[#1a1d24] rounded-3xl border border-gray-800 p-6 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-4">Добавить стикер в "{selectedPack.name}"</h2>
              <p className="text-sm text-gray-400 mb-4">
                Загрузи PNG изображение с прозрачным фоном (рекомендуется 512x512px)
              </p>
              <input
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={handleUploadSticker}
                disabled={uploadingSticker}
                className="w-full bg-[#0f1115] border border-gray-700 rounded-xl px-4 py-3 text-white mb-4 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-500"
              />
              {uploadingSticker && <p className="text-sm text-blue-400 mb-4 animate-pulse">Загрузка...</p>}
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedPack(null);
                }}
                className="w-full px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-xl font-bold transition"
              >
                Закрыть
              </button>
            </div>
          </div>
        )}

        {editingSticker && (
          <StickerEditorModal
            sticker={editingSticker}
            isOpen
            onClose={() => setEditingSticker(null)}
            onSaved={handleStickerSaved}
          />
        )}
      </main>
    </div>
  );
};

export default StickerPacksPage;
